// Domain -> wire mapping for the Hedging Grid CoPilot (CR-20260619-001, M2).
//
// Converts the idiomatic FE domain inputs (copilot-domain) into the backend wire DTOs
// (copilot-operations) at SEND time. Pure functions (unit-testable).
//
// Date encoding: questionnaire dates are CALENDAR dates (book/expiry days). They are serialized as
// "yyyy-MM-dd" using LOCAL components (getFullYear/getMonth/getDate), NOT toISOString() — the latter
// converts to UTC and can shift a picked calendar date across midnight (the classic off-by-one).

import {
    AdjustInputs,
    CloseInputs,
    FollowThroughSideInput,
    MaxRiskCeiling,
    PriceRange,
    SelectInputs
} from './copilot-domain';
import {
    CoPilotAdjustInputsDto,
    CoPilotFollowThroughSideDto,
    CoPilotMaxRiskCeilingDto,
    CoPilotPriceRangeDto,
    CoPilotSelectInputsDto,
    HmCloseInputsDto
} from './copilot-operations';

function pad2(n: number): string {
    return n < 10 ? '0' + n : '' + n;
}

// "yyyy-MM-dd" from LOCAL calendar components — TZ-drift-safe (see header).
export function toCalendarDateString(d: Date): string {
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
}

export function mapPriceRange(pr: PriceRange, defaultQuantity?: number): CoPilotPriceRangeDto {
    switch (pr.kind) {
        case 'bounded':
            // CR-20260820-002: the expected-range Range travels as its OWN kind with NO strikes.
            // Sending the panel's NaNs would be worse than sending nothing — they would deserialize
            // to a number the backend has no reason to distrust — and sending a boolean beside two
            // nulls would be invisible to a shell that predates the field.
            if (pr.useExpectedRange) {
                return { kind: 'expectedRange', startStrike: null, endStrike: null, direction: null };
            }
            return { kind: 'bounded', startStrike: pr.startStrike, endStrike: pr.endStrike, direction: null };
        case 'fillGap':
            return { kind: 'fillGap', startStrike: pr.startStrike, endStrike: pr.endStrike, direction: null };
        case 'followThrough':
            // THE TWO-SIDED FORM (CR-20260824-015). Each side's minimum is converted to PLAIN
            // DOLLARS here, per side, with the same conversion (and the same refuse-don't-guess
            // sentinel: 0 when a normalized figure has no lot to convert with) the run-level
            // minimum uses — the backend refuses a non-positive side minimum by name. Direction
            // travels null: the backend reads it on no form, and its absence beside the sides is
            // what an OLD backend refuses by name ("needs a direction"), which is the loud half
            // of the compatibility story.
            return {
                kind: 'followThrough',
                startStrike: null,
                endStrike: null,
                direction: null,
                followThroughUp: mapFollowThroughSide(pr.up, defaultQuantity),
                followThroughDown: mapFollowThroughSide(pr.down, defaultQuantity),
                closeRemainingPositions: pr.closeRemaining === true
            };
        case 'wall':
            return { kind: 'wall', startStrike: null, endStrike: null, direction: null };
        default:
            return { kind: 'wall', startStrike: null, endStrike: null, direction: null };
    }
}

// One follow-through side to the wire, or null for a disabled one. An enabled side always
// travels enabled: true — the null/object split IS the enable flag's wire form, and the boolean
// restates it so the backend's reader never has to infer.
function mapFollowThroughSide(
    side: FollowThroughSideInput | null,
    defaultQuantity: number | undefined
): CoPilotFollowThroughSideDto | null {
    if (side == null) {
        return null;
    }

    return {
        enabled: true,
        startStrike: isFinite(side.startStrike) ? side.startStrike : null,
        minProfitDollars: toPlainDollarsOrZero(side.minProfit, side.minProfitNormalized,
            defaultQuantity),
        harvest: side.harvest === true
    };
}

// THE WIRE CARRIES PLAIN DOLLARS, ALWAYS (2026-08-13, the owner's default-quantity ruling).
// The Normalized typing mode survives as a DISPLAY convenience only and is converted HERE, on the
// way out — one place, so the backend has one meaning to read and needs no divisor to read it.
export function mapMaxRiskCeiling(mr: MaxRiskCeiling, defaultQuantity: number | undefined): CoPilotMaxRiskCeilingDto {
    if (mr.kind === 'dollar') {
        // ⚠ NULL, NEVER 0, WHEN THE CONVERSION IS IMPOSSIBLE. Zero is a REAL max-risk answer here
        // (owner, 2026-07-27: "zero is a real answer, not a missing one"), so it cannot double as
        // the "could not convert" sentinel — that would silently turn an unconvertible request into
        // a demand for zero risk. Absent amount is what the backend refuses by name.
        //
        // The dollar ceiling carries its placement LEVEL on the wire since CR-20260819-002 (R-2) —
        // the DTO field always existed; the dollar arm now populates it. Never 'Low': the domain
        // type cannot hold it, and the backend refuses a dollar 'Low' by name (a $0 amount is the
        // dollar spelling of no-risk). A level-less dollar ceiling from an older client reads as
        // Aggressive backend-side (R-11), which is what the arm always shipped.
        return { kind: 'dollar', amount: toPlainDollars(mr.amount, mr.normalized, defaultQuantity), level: mr.level };
    }
    return { kind: 'relative', amount: null, level: mr.level };
}

// A figure the trader typed, as plain dollars.
//
// A NORMALIZED figure is measured in units of "one default lot's worth per point", so it becomes
// dollars by multiplying back by (defaultQty x contract multiplier) — the exact inverse of the
// division the backend mapper used to perform. Doing it here rather than there is the whole of the
// ruling: *"default quantity is only useful when you calculate the final price of the package,
// which happens on the GUI side anyway."*
//
// ⚠ NO USABLE DIVISOR = NULL, DELIBERATELY. A normalized figure cannot be converted without the
// portfolio's lot, and guessing one would silently mis-size the trade by whatever factor was
// guessed. NULL — not 0 — because 0 is a meaningful amount for max risk and would be indistinguishable
// from a real answer. The backend refuses an absent figure by name; see toPlainDollarsOrZero for the
// minimum-profit path, where the wire field is non-nullable.
export function toPlainDollars(
    amount: number | null | undefined,
    normalized: boolean,
    defaultQuantity: number | undefined
): number | null {
    if (typeof amount !== 'number' || isNaN(amount)) {
        return null;
    }
    if (!normalized) {
        return amount;   // already what the wire wants
    }
    if (typeof defaultQuantity !== 'number' || !isFinite(defaultQuantity) || defaultQuantity <= 0) {
        return null;
    }
    return amount * defaultQuantity * CONTRACT_MULTIPLIER;
}

// The same conversion for the minimum profit, whose wire field is a plain number. 0 is NOT a
// meaningful minimum profit — a request for no profit is economic nonsense — so here it is a safe
// sentinel, and the backend refuses a non-positive minimum by name.
export function toPlainDollarsOrZero(
    amount: number | null | undefined,
    normalized: boolean,
    defaultQuantity: number | undefined
): number {
    const converted = toPlainDollars(amount, normalized, defaultQuantity);
    return converted === null ? 0 : converted;
}

// SPX options are worth $100 per point per contract. Mirrors PnlLine.ContractMultiplier on the
// backend — the two are the same boundary constant and must move together.
export const CONTRACT_MULTIPLIER = 100;

// mapSelectInputs was DELETED with the Select wire path (CR-20260804-001, ruling AF): the
// Select tab fires the Select Copilot's own operation, so nothing builds a Select DTO here.

// defaultQuantity comes from the loaded portfolio (facade readDefaultQty), NOT from the questionnaire
// — the trader never types it. It is passed in rather than read here so this stays a pure function.
//
// ⚠⚠ IT NO LONGER CROSSES THE WIRE (2026-08-13). It is consumed HERE, converting whatever the trader
// typed into plain dollars, and never leaves the panel. The backend has no divisor and needs none.
//
// When it cannot be resolved AND the trader typed a normalized figure, the conversion is impossible:
// the minimum profit goes out as 0 and the max-risk amount as null, both of which the backend
// refuses BY NAME rather than reinterpreting against a guessed divisor.
// ⚠ i.expiration IS DELIBERATELY NOT MAPPED (CR-20260814-001). It is not an omission — do not "fix" it.
// The selected expiration acts by narrowing the POSITIONS sent on RunCoPilotSkill
// (hg-copilot.component.positionsForAdjust), and the backend then derives the run's expiration from
// those positions exactly as it did before the selector existed. Putting it on the wire as well would
// be redundant, and would cost the backstop: today a frontend filtering bug trips AdjustRunMapper's
// "more than one expiration" refusal by name, whereas an explicit wire field would let the backend
// trust the label over the payload and solve the wrong P&L line silently.
export function mapAdjustInputs(i: AdjustInputs, defaultQuantity: number | undefined): CoPilotAdjustInputsDto {
    return {
        priceRange: mapPriceRange(i.priceRange, defaultQuantity),
        minProfitDollars: toPlainDollarsOrZero(i.minProfit, i.minProfitNormalized, defaultQuantity),
        maxRisk: mapMaxRiskCeiling(i.maxRisk, defaultQuantity),
        harvest: i.harvest === true,
        // Sent as the trader picked it, 'auto' included — the backend resolves auto BY PRICE
        // (CR-20260819-001, both arms since CR-20260819-002), and it is the backend's trace that
        // has to say what was asked before saying what won. Rewriting it here would put a side in
        // the log nobody chose.
        riskSide: i.riskSide || 'both',
        halfRisk: i.halfRisk === true,
        // CR-20260820-002. Only on the form that has a band to scale — every other form would be
        // sending a setting nothing reads, and a field nothing reads is a field some future client
        // will set and expect to work.
        expectedRangeModel: i.priceRange && i.priceRange.kind === 'bounded'
            && i.priceRange.useExpectedRange && i.expectedRangeModel
            ? {
                model: i.expectedRangeModel.model,
                customPercent: i.expectedRangeModel.customPercent
            }
            : null
    };
}

// The close questionnaire for RunHedgeMatrixCloseSkill (CR-20260910-001 milestone 2).
//
// The shape is unchanged; only its destination is. It used to fill the dormant close slot on the
// Adjust command, which the backend carried and never read — now the Close is its own command with
// its own input model, and this fills that. Dates were already calendar strings, so the mapping
// itself needed nothing.
export function mapCloseInputs(i: CloseInputs): HmCloseInputsDto {
    return {
        dates: i.dates.map(toCalendarDateString),
        targetKind: i.target.kind,
        hedgeIds: i.target.kind === 'hedges' ? i.target.hedgeIds : []
    };
}
